// Shared by the single-product buy box (product-info.tsx) and the Insider
// Bundle add flow (hydrate-bundle): turning the CMS Subscribe & Save gifts
// into cart lines / selection prompts.

import type { GiftSelection } from '@/components/product/gift-variant-modal';
import type { SubscribeSaveConfig } from '@/lib/subscribe-save-config';

/**
 * Cart lines for the CMS gifts that resolved to a live variant, added in the
 * same mutation as a subscription line. Gifts already in the cart are skipped
 * so a second add never stacks gift quantities.
 */
export function buildGiftExtraLines(
	gifts: SubscribeSaveConfig['gifts'],
	cart: { lines: { edges: { node: { merchandise: { id: string } } }[] } } | null,
	currencyCode: string,
) {
	return gifts
		.filter(
			(gift): gift is (typeof gifts)[number] & { variantId: string } =>
				!!gift.variantId &&
				!cart?.lines.edges.some((edge) => edge.node.merchandise.id === gift.variantId),
		)
		.map((gift) => ({
			merchandiseId: gift.variantId,
			quantity: 1,
			productInfo: {
				title: gift.title,
				handle: gift.productHandle,
				image: gift.imageUrl ? { url: gift.imageUrl, altText: gift.imageAlt } : null,
				price: gift.variantPrice ?? { amount: '0.0', currencyCode },
				attributes: [{ key: '_free_gift', value: 'true' }],
			},
		}));
}

/**
 * Multi-variant gifts (e.g. a free tee needing a size) can't auto-add — they
 * open the selection sheet after the subscription lands in the cart. Gifts
 * whose product is already a cart line are skipped.
 */
export function buildGiftSelections(
	gifts: SubscribeSaveConfig['gifts'],
	cartAfterAdd: {
		lines: { edges: { node: { merchandise: { product: { handle: string } } } }[] };
	} | null,
): GiftSelection[] {
	return gifts
		.filter((gift) => gift.requiresSelection && gift.productHandle)
		.filter(
			(gift) =>
				!cartAfterAdd?.lines.edges.some(
					(edge) => edge.node.merchandise.product.handle === gift.productHandle,
				),
		)
		.map((gift) => ({ productHandle: gift.productHandle, title: gift.title }));
}
